import {
  Component,
  effect,
  EventEmitter,
  Injector,
  input,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { BreadcrumbsService } from '../../../../../../../../../../../SUVO-CORE-CLIENT-LIB/projects/suvo-core-client-lib/src/lib/features/breadcrumbs/services/breadcrumbs.service';
import { IDataSourceDefinition } from '../../../../../../shared/interfaces/data-source/data-source-definitions/data-source-definition.interface';
import { IGridSettings } from '../../../../../smart-grid/interfaces/grid-settings.interfaces';
import { IUIDataSourceItem } from '../../../../data-source-items/interfaces/ui-data-source-item';
import { NewDataSourceDefinitionsService } from '../../../../data-source-definitions/services/new-data-source-definitions.service';
import { DataSourceFavouritesService } from '../../../../data-source-favourites/services/data-source-favourites.service';
import { NewDataSourceItemsService } from '../../../../data-source-items/services/new-data-source-items.service';
import { IDataSourceItemView } from '../../../interfaces/data-source-item-view.interface';
import { IViewTileDefinition } from '../../../interfaces/view-tile-definition.interface';
import { ViewTileType } from '../../../interfaces/view-tile-type.enum';
import { DataSourceItemViewsService } from '../../../services/data-source-item-views.service';
import { ItemFormsService } from '../../../services/item-forms.service';

@Component({
  standalone: false,
  selector: 'suvo-bi-data-source-item-view',
  templateUrl: './data-source-item-view.component.html',
  styleUrls: ['./data-source-item-view.component.scss'],
})
export class DataSourceItemViewComponent implements OnInit, OnDestroy {
  @Input() admin = false;
  @Output() itemTitleResolved = new EventEmitter<string>();
  @Output() itemNamePropertyConfigured = new EventEmitter<boolean>();

  readonly alias = input.required<string>();
  readonly itemId = input.required<string>();

  view: IDataSourceItemView;
  item: IUIDataSourceItem;
  definition: IDataSourceDefinition;
  isLoading = true;

  dataForm: FormGroup;

  gridSettings: IGridSettings = {
    maxColumnCount: 3,
    canEdit: true,
    canEditJson: true,
  };
  editGridMode = false;
  editItemMode = false;

  private favouriteBreadcrumbVisible = false;

  context = {
    canEditView: false,
    canEditItem: false,
  };

  stillHere = true;

  constructor(
    private breadcrumbsService: BreadcrumbsService,
    private dataSourceItemViewsService: DataSourceItemViewsService,
    private dataSourceItemsService: NewDataSourceItemsService,
    private dataSourceDefinitionsService: NewDataSourceDefinitionsService,
    private itemFormsService: ItemFormsService,
    private injector: Injector,
  ) {}

  private readonly onItemChange = effect(() => {
    const alias = this.alias();
    const itemId = this.itemId();
    if (alias && itemId) {
      this.load(alias, itemId);
    }
  });

  ngOnInit() {
    this.context.canEditItem = this.admin;
    this.context.canEditView = false;
  }

  async load(definitionAlias: string, itemId: string) {
    this.isLoading = true;
    this.view = null;
    this.item = null;
    this.definition = null;

    const forceStandardView = this.shouldForceStandardView(definitionAlias);

    let [viewLoaded, item, definition] = await Promise.all([
      forceStandardView ? Promise.resolve(null) : this.dataSourceItemViewsService.getOne(null, definitionAlias),
      this.dataSourceItemsService.getOne(definitionAlias, itemId),
      this.dataSourceDefinitionsService.getOne('alias', definitionAlias),
    ]);

    let viewCreated: IDataSourceItemView | null = null;
    if ((!viewLoaded || forceStandardView) && definition) {
      viewCreated = this.createDefaultView(definition, definitionAlias);
    }

    if (!(viewLoaded || viewCreated) || !item || !definition) {
      this.itemNamePropertyConfigured.emit(false);
      this.itemTitleResolved.emit('');
      this.isLoading = false;
      return;
    }

    if (!this.admin && this.stillHere) {
      const dataSourceFavouritesService = this.injector.get(DataSourceFavouritesService);
      dataSourceFavouritesService.setBreadcrumbStarForItemId(definitionAlias, itemId);
      this.favouriteBreadcrumbVisible = true;
    }

    const itemNameProperty = definition.displayOptions?.itemNameProperty;
    const hasItemNameProperty = typeof itemNameProperty === 'string' && !!itemNameProperty.trim();
    this.itemNamePropertyConfigured.emit(hasItemNameProperty);

    const drawerTitle = this.resolveDrawerTitle(item, definition, definitionAlias);
    this.itemTitleResolved.emit(drawerTitle);

    const itemBreadcrumbName = this.resolveItemBreadcrumbName(item, definition, definitionAlias);
    this.breadcrumbsService.setDynamicNameSwap('item-name', itemBreadcrumbName as string);
    this.breadcrumbsService.setDynamicNameSwap('definition-name', definition.name);

    this.view = viewLoaded
      ? (viewLoaded as unknown as IDataSourceItemView)
      : (viewCreated as IDataSourceItemView);
    this.item = item;
    this.definition = definition;
    this.isLoading = false;
  }

  private shouldForceStandardView(alias: string): boolean {
    return /^tenders([_-].+)?$/i.test(alias ?? '');
  }

  private resolveItemBreadcrumbName(
    item: IUIDataSourceItem,
    definition: IDataSourceDefinition,
    alias: string,
  ): string {
    const itemNameProperty = definition.displayOptions?.itemNameProperty;
    const configuredValue =
      itemNameProperty &&
      item?.data &&
      Object.prototype.hasOwnProperty.call(item.data, itemNameProperty)
        ? item.data[itemNameProperty]
        : null;
    const configuredString =
      configuredValue === null || configuredValue === undefined
        ? null
        : String(configuredValue).trim();

    if (configuredString) {
      return configuredString;
    }

    const friendlyTenderId = this.resolveFriendlyTenderId(item, alias);
    if (friendlyTenderId) {
      return friendlyTenderId;
    }

    if (item?._id) {
      return item._id;
    }

    return '?';
  }

  private resolveDrawerTitle(
    item: IUIDataSourceItem,
    definition: IDataSourceDefinition,
    alias: string,
  ): string {
    const itemNameProperty =
      typeof definition.displayOptions?.itemNameProperty === 'string'
        ? definition.displayOptions.itemNameProperty.trim()
        : '';

    if (!itemNameProperty || !item?.data) {
      return this.resolveFriendlyTenderId(item, alias) ?? '';
    }

    if (!Object.prototype.hasOwnProperty.call(item.data, itemNameProperty)) {
      return this.resolveFriendlyTenderId(item, alias) ?? '';
    }

    const configuredValue = item.data[itemNameProperty];
    const configuredString =
      configuredValue === null || configuredValue === undefined
        ? ''
        : String(configuredValue).trim();

    return configuredString || this.resolveFriendlyTenderId(item, alias) || '';
  }

  private resolveFriendlyTenderId(item: IUIDataSourceItem, alias: string): string | null {
    if (!this.shouldForceStandardView(alias) || !item?.data) {
      return null;
    }

    const candidates = [
      item.data.friendlyId,
      item.data.tenderNoticeNumber,
      item.data.tender_notice_no,
      item.data.refid,
    ];

    for (const candidate of candidates) {
      if (candidate !== null && candidate !== undefined) {
        const normalized = String(candidate).trim();
        if (normalized) {
          return normalized;
        }
      }
    }

    return null;
  }

  createDefaultView(definition: IDataSourceDefinition, alias: string) {
    const showHiddenProperties = this.admin;
    const hiddenTenderProperties = new Set(['itemdetails', 'itemDetails', 'tendersDetails']);

    const tileDefinition: IViewTileDefinition = {
      tileType: ViewTileType.Properties,
      propertyKeys: (showHiddenProperties
        ? definition.properties
        : definition.properties.filter((p) => !p.isHidden)
      )
        .filter((property) => {
          if (!this.shouldForceStandardView(alias)) {
            return true;
          }

          return !hiddenTenderProperties.has(property.name);
        })
        .map((property) => ({ property: property.name })),
    };

    const defaultView: IDataSourceItemView = {
      sections: [{ rows: [{ columns: [{ tileDefinition }] }] }],
    };

    return defaultView;
  }

  setEditItemMode(enabled: boolean) {
    if (enabled) {
      if (!this.dataForm) {
        const dataForm = this.itemFormsService.createDefinitionDataForm(this.definition);
        dataForm.patchValue(this.item.data);
        this.dataForm = dataForm;
      }
    }

    this.editItemMode = enabled;
  }

  onEditItemToggle(event: MatSlideToggleChange) {
    if (!this.admin) return;
    if (this.editItemMode === event.checked) return;
    this.setEditItemMode(event.checked);
  }

  onSaveGrid() {
    return (view: IDataSourceItemView) => {
      return this.dataSourceItemViewsService.patchOne(null, view._id, view);
    };
  }

  ngOnDestroy() {
    this.stillHere = false;

    this.breadcrumbsService.removeDynamicNameSwap('item-name');
    this.breadcrumbsService.removeDynamicNameSwap('definition-name');

    this.onItemChange.destroy();

    if (this.favouriteBreadcrumbVisible) {
      const dataSourceFavouritesService = this.injector.get(DataSourceFavouritesService);
      dataSourceFavouritesService.removeBreadcrumbStar();
    }
  }
}
